// core version + navigation, pagination modules:
import Swiper from 'swiper'
import { Navigation, Thumbs } from 'swiper/modules'

// Module-level: YouTube IFrame API postMessage tracking for swiper gallery iframes.
// The listener is registered once per page load regardless of how many times
// initSwiperGallery is called (e.g. after AJAX variant switches).
;(function () {
    // WeakSet prevents double-subscription: onReady fires once per iframe load,
    // but guard ensures postMessage addEventListener:onStateChange is sent only once
    // even if YouTube ever re-sends onReady (e.g. network replay).
    const subscribedWindows = new WeakSet()

    // Track which iframe elements have already fired video_play this page load.
    // Prevents re-firing when the user pauses and resumes the same video.
    const ga4FiredIframes = new WeakSet()

    // pointerdown fires on the iframe element in the parent document before focus
    // transfers into the iframe — use this to distinguish user-initiated plays from
    // autoplay (which produces onStateChange=1 without a preceding pointer event).
    const pendingUserClickIframes = new WeakSet()

    document.addEventListener('pointerdown', function (e) {
        const iframe = e.target.closest('.swiper-gallery .youtube-video')
        if (iframe) pendingUserClickIframes.add(iframe)
    })

    function subscribeToStateChange(iframeWindow) {
        if (subscribedWindows.has(iframeWindow)) return
        subscribedWindows.add(iframeWindow)
        iframeWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
            '*'
        )
    }

    window.addEventListener('message', function (event) {
        if (event.origin !== 'https://www.youtube.com') return

        let data
        try {
            data = JSON.parse(event.data)
        } catch (e) {
            return
        }

        // YouTube signals player is ready — subscribe to state changes only for
        // swiper gallery iframes; hero-video and areas/videos iframes are ignored.
        if (data.event === 'onReady') {
            const swiperIframes = document.querySelectorAll('.swiper-gallery .youtube-video')
            const isSwiper = Array.from(swiperIframes).some(
                (iframe) => iframe.contentWindow === event.source
            )
            if (isSwiper) subscribeToStateChange(event.source)
            return
        }

        // State 1 = playing; only handle swiper gallery iframes
        if (data.event !== 'onStateChange' || data.info !== 1) return

        const youtubeIframes = document.querySelectorAll('.swiper-gallery .youtube-video')
        const playingIframe = Array.from(youtubeIframes).find(
            (iframe) => iframe.contentWindow === event.source
        )

        // Guard: ignore messages from non-swiper iframes (hero-video, areas/videos)
        if (!playingIframe) return

        // Fire only once per iframe per page load — ignore pause → resume replays
        if (ga4FiredIframes.has(playingIframe)) return

        // Only fire for user-initiated plays — suppress autoplay
        if (!pendingUserClickIframes.has(playingIframe)) return
        pendingUserClickIframes.delete(playingIframe)

        ga4FiredIframes.add(playingIframe)
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            event: 'video_play',
            video_title: playingIframe.getAttribute('data-video-title') || '',
            video_percent: 0,
        })
    })
})()

// ✅ Expose globally
window.initSwiperGallery = function () {
    let module = $('.swiper-gallery')

    // Only continue if module is on page
    if (!module.length) return

    module.each(function () {
        const $this = $(this)
        const carouselSelector = '.swiper-carousel'
        const thumbnailsSelector = '.swiper-thumbnails'
        const zoomSelector = '.swiper-zoom'
        const imageCarousel = $this.find(carouselSelector)
        const imageZoomButton = $this.find('.zoom-button')
        const imageCloseButton = $this.find('.close-button')
        let activeImageIndex = 0

        const thumbnailsInstance = new Swiper(thumbnailsSelector, {
            modules: [Navigation],
            spaceBetween: 10,
            slidesPerView: 6,
            freeMode: true,
            direction: 'vertical',
            watchSlidesProgress: true,
            navigation: {
                nextEl: '.swiper-next',
                prevEl: '.swiper-prev',
            },
            on: {
                slideChange: function (e) {
                    activeImageIndex = e.realIndex
                },
            },
        })

        new Swiper(carouselSelector, {
            modules: [Navigation, Thumbs],
            spaceBetween: 10,
            navigation: {
                nextEl: '.swiper-next',
                prevEl: '.swiper-prev',
            },
            thumbs: {
                swiper: thumbnailsInstance,
            },
            on: {
                slideChange: function (e) {
                    activeImageIndex = e.realIndex
                    const video = imageCarousel.find('.video-playing video')
                    if (video.length) stopHtml5Video(video)
                    const youtubeVideos = imageCarousel.find('.youtube-video')
                    if (youtubeVideos.length) pauseYoutubeVideos(youtubeVideos)
                },
            },
        })

        new Swiper(zoomSelector, {
            modules: [Navigation],
            spaceBetween: 10,
            navigation: {
                nextEl: '.swiper-next',
                prevEl: '.swiper-prev',
            },
            on: {
                slideChange: function () {
                    pauseVideos(zoomSelector)
                },
            },
        })

        // Launch product modal when click on zoom button
        imageZoomButton.click(() => {
            pauseVideos(carouselSelector)
            showProductModal(true, activeImageIndex)
        })

        // Close product modal when click on close button
        imageCloseButton.click(() => {
            pauseVideos(zoomSelector)
            showProductModal(false)
        })

        // Play html5 video when the play icon overlay is clicked
        $this.on('click', '.video-play-icon', function () {
            const wrapper = $(this).closest('.swiper-video-wrapper')
            const video = wrapper.find('video')[0]
            if (!video) return
            video.controls = true
            video.play()
            wrapper.closest('.swiper-slide').addClass('video-playing')
        })

    })

    function stopHtml5Video(elem) {
        elem[0].load()
        elem.prop('controls', false)
        elem.closest('.swiper-slide').removeClass('video-playing')
    }

    function showProductModal(visible, activeImageIndex = 0) {
        $('.product-modal').modal(visible ? 'show' : 'hide')
        if (visible) {
            document.querySelector('.swiper-zoom').swiper.slideTo(activeImageIndex)
        }
    }

    function pauseVideos(carousel) {
        if (carousel) {
            const video = $(carousel).find('.video-playing video')
            if (video.length) stopHtml5Video(video)
            const youtubeVideos = $(carousel).find('.youtube-video')
            if (youtubeVideos.length) pauseYoutubeVideos(youtubeVideos)
        }
    }

    function pauseYoutubeVideos(youtubeVideos) {
        for (const iframe of youtubeVideos) {
            const iframeContent = iframe.contentWindow || iframe.contentDocument
            if (iframeContent && iframeContent.postMessage) {
                iframeContent.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
            }
        }
    }
}
